/**
 * Formatting Utilities
 *
 * Centralized formatting functions for currency, percentages, dates, and numbers.
 */

import { UI_CONFIG } from "@/lib/constants";

/**
 * Format a number as currency
 * @param value - The value to format
 * @param showCents - Whether to show cents (default: true)
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(
  value: number | string | null | undefined,
  showCents: boolean = true,
): string {
  if (value === null || value === undefined) return showCents ? "$0.00" : "$0";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return showCents ? "$0.00" : "$0";

  return new Intl.NumberFormat(UI_CONFIG.CURRENCY_LOCALE, {
    style: "currency",
    currency: UI_CONFIG.CURRENCY,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(numValue);
}

/**
 * Format a number as a compact currency (e.g., $1.2M)
 * @param value - The value to format
 * @returns Formatted compact currency string
 */
export function formatCurrencyCompact(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined) return "$0";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "$0";

  return new Intl.NumberFormat(UI_CONFIG.CURRENCY_LOCALE, {
    style: "currency",
    currency: UI_CONFIG.CURRENCY,
    notation: "compact",
    maximumSignificantDigits: 3,
  }).format(numValue);
}

/**
 * Format currency rounded to nearest dollar (no cents)
 * Alias for formatCurrency with showCents=false
 * @param value - The value to format
 * @returns Formatted currency string without cents (e.g., "$1,234")
 */
export function formatCurrencyRounded(
  value: number | string | null | undefined,
): string {
  return formatCurrency(value, false);
}

/**
 * Format currency in compact notation (e.g., "$1.2M", "$450K")
 * @param value - The value to format
 * @returns Formatted compact currency string
 */
export function formatCompactCurrency(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined) return "—";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "—";

  return new Intl.NumberFormat(UI_CONFIG.CURRENCY_LOCALE, {
    style: "currency",
    currency: UI_CONFIG.CURRENCY,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(numValue);
}

/**
 * Safely convert a value to number, handling null/undefined/NaN
 * @param value - Value to convert
 * @param defaultValue - Default value if conversion fails (default: 0)
 * @returns Number value
 */
export function safeNumber(
  value: number | string | null | undefined,
  defaultValue: number = 0,
): number {
  if (value === null || value === undefined) return defaultValue;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? defaultValue : num;
}

/**
 * Format a number as a percentage
 * @param value - The value to format (can be decimal like 0.05 or whole like 5)
 * @param decimals - Number of decimal places (default: 2)
 * @param isDecimal - Whether the input is already a decimal (0.05 vs 5)
 * @returns Formatted percentage string (e.g., "5.00%")
 */
export function formatPercentage(
  value: number | string | null | undefined,
  decimals: number = 2,
  isDecimal: boolean = false,
): string {
  if (value === null || value === undefined) return "0.00%";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "0.00%";

  // If the value is already a percentage (like 5 for 5%), use it directly
  // If it's a decimal (like 0.05 for 5%), convert it
  const percentage = isDecimal ? numValue * 100 : numValue;

  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Format a date string
 * @param value - The date string to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
): string {
  if (!value) return "N/A";

  try {
    const date = typeof value === "string" ? new Date(value) : value;
    if (isNaN(date.getTime()))
      return typeof value === "string" ? value : "Invalid Date";

    return date.toLocaleDateString(UI_CONFIG.CURRENCY_LOCALE, options);
  } catch {
    return typeof value === "string" ? value : "Invalid Date";
  }
}

/**
 * Format a date as relative time (e.g., "2 days ago")
 * @param value - The date to format
 * @returns Relative time string
 */
export function formatRelativeTime(
  value: string | Date | null | undefined,
): string {
  if (!value) return "N/A";

  try {
    const date = typeof value === "string" ? new Date(value) : value;
    if (isNaN(date.getTime())) return "Invalid Date";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  } catch {
    return "Invalid Date";
  }
}

/**
 * Format a number with commas
 * @param value - The number to format
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export function formatNumber(
  value: number | string | null | undefined,
  decimals: number = 0,
): string {
  if (value === null || value === undefined) return "0";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "0";

  return new Intl.NumberFormat(UI_CONFIG.CURRENCY_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
}

/**
 * Format square footage
 * @param value - The square footage value
 * @returns Formatted string (e.g., "1,500 sqft")
 */
export function formatSquareFeet(
  value: number | string | null | undefined,
): string {
  const formatted = formatNumber(value);
  return `${formatted} sqft`;
}

/**
 * Parse a potentially string number to a number
 * @param value - The value to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed number
 */
export function parseNumber(
  value: string | number | null | undefined,
  defaultValue: number = 0,
): number {
  if (value === null || value === undefined || value === "")
    return defaultValue;
  if (typeof value === "number") return isNaN(value) ? defaultValue : value;

  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Format address components into a full address string
 * @param components - Address components
 * @returns Formatted address string
 */
export function formatAddress(components: {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}): string {
  const parts: string[] = [];

  if (components.address_line1) {
    parts.push(components.address_line1);
  }
  if (components.address_line2) {
    parts[parts.length - 1] += `, ${components.address_line2}`;
  }

  const cityStateZip: string[] = [];
  if (components.city) cityStateZip.push(components.city);
  if (components.state) cityStateZip.push(components.state);
  if (components.zip_code) cityStateZip.push(components.zip_code);

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  return parts.join(", ");
}
